import type { DateInput } from './date'

export function first(items?: unknown[] | CmsData | null) {
  // Both members of the union are indexable at runtime, but the union itself is not, so index one.
  return (items as CmsData | undefined)?.[0] || {}
}

/**
 * Strip the fragment from a router `fullPath`.
 *
 * Browsers never send the fragment to the server, so `route.fullPath` is inherently different on the two
 * sides of an SSR render: `/page?x=1` on the server, `/page?x=1#anchor` on the client. Using it directly
 * for anything the server also computes - a `useAsyncData` key, a `NuxtPage` page key, a CMS uri - makes
 * the client miss the server's payload entry and produces a hydration mismatch on the whole page. Use
 * this wherever the value has to match across SSR and hydration - including values that only look
 * client-side, such as a login `returnUrl` that ends up in a server-rendered `href`, or a stored path a
 * getter later compares against to mark the active navigation item. Plain `fullPath` is only safe where
 * nothing server-rendered depends on it, such as an imperative `navigateTo` on click.
 */
export function pathWithoutHash(fullPath: string) {
  return fullPath.replace(/#.*$/, '')
}

export function hasFirst(items: unknown) {
  return Array.isArray(items) && items.length > 0
}

export function isEmpty(item?: object | null) {
  return item === null || item === undefined || Object.keys(item).length === 0
}

export function toStringArray(value: unknown) {
  if (typeof value === 'string') {
    return [value]
  }
  if (Array.isArray(value) && value.every((val) => typeof val === 'string')) {
    return value
  }

  return []
}

export function toNumber(value: unknown, fallback = 0) {
  if (typeof value === 'string') {
    return Number.parseInt(value)
  }

  if (typeof value === 'number') {
    return value
  }

  return fallback
}

/*
 * `en-CA` is picked purely for its output shape: Canadian English writes dates as YYYY-MM-DD, so this
 * formatter yields `2026-09-30` without any manual assembly. (`sv-SE` is the other locale commonly used
 * for this.) The tests pin the format, so an ICU/CLDR change would fail CI rather than slip through.
 */
const centralEuropeanDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Minutes Central European time is ahead of UTC at the given instant: 60 for CET, 120 for CEST. */
function centralEuropeanOffsetMinutes(instant: Date) {
  const offset = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Amsterdam', timeZoneName: 'longOffset' })
    .formatToParts(instant)
    .find((part) => part.type === 'timeZoneName')?.value
  const [, sign, hours, minutes] = offset?.match(/GMT([+-])(\d{2}):(\d{2})/) ?? []

  if (!sign) {
    return 0
  }

  return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
}

export function getDay(date: Date | null | undefined, fallback: string | undefined = undefined) {
  if (!date) {
    return fallback
  }
  // `toJSON()` returns null for an invalid Date, which is truthy above and so gets past the guard.
  const parts = date.toJSON()?.match(/(\d+)/g)
  // parts[0], parts[1]-1, parts[2] because months are 0 based
  return parts?.[2] ?? fallback
}

export function toDateString(date: Date | null | undefined, fallback: string | undefined = undefined) {
  if (!date) {
    return fallback
  }
  const day = getDay(date)
  if (day === undefined) {
    return fallback
  }

  return [
    date.getFullYear().toLocaleString('en-US', { minimumIntegerDigits: 4, useGrouping: false }),
    (date.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }),
    /*
     * Deliberately not `date.getDate()`, which reports the day in local time: a UTC instant late in the
     * evening then reads as the next day, and a DST switch moves where that boundary falls. `getDay()`
     * takes the day from the UTC ISO string instead, and already returns it zero-padded.
     */
    day,
  ].join('-')
}

/**
 * Return the YYYY-MM-DD calendar date of an instant, as it reads at the venue.
 *
 * "Local" here means Central European time, not the machine's own zone: CET in winter, CEST in summer.
 * Both conversions go through `Intl` with an explicit `timeZone`, so the result is the same wherever the
 * code runs - these are called client-side as well as during SSR, and a visitor's browser is rarely on UTC.
 */
export function toLocalDateString(utcDate: DateInput, fallback: string | undefined = undefined) {
  if (!utcDate) {
    return fallback
  }

  const date = toDate(utcDate)
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return fallback
  }

  // The `en-CA` locale formats as YYYY-MM-DD, which is exactly the shape we need.
  return centralEuropeanDateFormatter.format(date)
}

// The fallback is returned untouched, so its type is whatever the caller passes.
export function fromLocalDateString<T = undefined>(localDateString: string, fallback?: T) {
  if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(localDateString)) {
    return fallback
  }

  /*
   * Take midnight UTC on that calendar day, then subtract whichever offset is in effect at that moment
   * (+1 in winter, +2 in summer) to land on the instant that local midnight actually corresponds to.
   */
  const utcMidnight = new Date(`${localDateString}T00:00:00.000Z`)
  return new Date(utcMidnight.getTime() - centralEuropeanOffsetMinutes(utcMidnight) * 60_000)
}

// The fallback is returned untouched, so its type is whatever the caller passes.
export function toDate<T = undefined>(value: DateInput, fallback?: T) {
  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'string') {
    switch (value) {
      case 'now':
        return new Date()
      case 'today': {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        return date
      }
      default:
        return new Date(value)
    }
  }

  return fallback
}

// `event` is the H3Event from `useRequestEvent()`, captured by the caller before any await (it is
// itself a Nuxt composable and unusable afterwards).
export function setCacheControlHeader(event: ReturnType<typeof useRequestEvent>, mustRevalidate = false) {
  if (import.meta.server) {
    event?.node.res.setHeader('Cache-Control', `public, s-maxage=60,${mustRevalidate ? ' must-revalidate,' : ''} stale-while-revalidate=60, stale-if-error=60`)
  }
}
